import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { attachJob, selectJobByCourse } from '@/features/ai/jobsSlice'
import { useGetQuoteQuery } from '@/features/ai/studioApi'
import type { StudioJobType } from '@/features/ai/studioTypes'

/**
 * Проактивный гейт «курс занят другим прогоном» (Task 8). Источник —
 * `quote.active_job`: тот же, что кормит 409 на бэке, поэтому расхождения
 * «панель говорит можно, а старт отказал» не будет.
 *
 * Курс занят ЧУЖИМ прогоном, если запись реестра указывает на другой job_id
 * (или её нет вовсе). Если активная джоба — НАША привязанная, панель и так
 * рисует прогресс, а второй баннер поверх него был бы шумом.
 *
 * М-5: читает реестр напрямую (`selectJobByCourse` + `dispatch`), а не через
 * весь `useCourseJob` — тому гейту не нужен опрос статуса чужой джобы
 * (`useAiChat` уже подписан на неё своим `useCourseJob`, вторая подписка на
 * тот же `getStudioJob` была бы лишней связностью).
 *
 * Довод «лишняя связность» — именно про ЭТОТ случай, не общее правило
 * (поправлено M-6, финальное ревью, иначе следующий читатель счёл бы вторую
 * подписку где угодно ошибкой): `active_job` здесь — ЧУЖОЙ прогон, чью джобу
 * `useCourseJob` вообще не опрашивает, а сама смета уже несёт всё нужное
 * (id/тип/статус). У `useAutoOpenForJob.ts`, наоборот, подписка НА ТОТ ЖЕ
 * `jobId`, что и у `useCourseJob`, — она осознанная (Important 2) и не
 * заводит второй запрос: RTK Query делит кеш между подписчиками одного ключа.
 */
export const useCourseBusyGate = (courseId: number, isOpen: boolean) => {
  const dispatch = useAppDispatch()
  // Не грузим смету, пока панель свёрнута — гейту всё равно нечего
  // показывать. `refetchOnMountOrArgChange` (M-2): гейт обязан отражать
  // МОМЕНТ ОТКРЫТИЯ панели, а не смету, закешированную с прошлого раза.
  const { data: quote } = useGetQuoteQuery(courseId, {
    skip: !isOpen,
    refetchOnMountOrArgChange: true,
  })
  const entry = useAppSelector((s) => selectJobByCourse(s, courseId))
  const activeJob = quote?.active_job ?? null
  const isBusy = activeJob !== null && entry?.jobId !== activeJob.job_id

  /** Перецепляет реестр курса на чужой прогон — из проактивного гейта и из
   *  баннера отказа 409 (страховка на случай гонки со сметой). */
  const showRun = (jobId: number, jobType: StudioJobType) =>
    dispatch(
      attachJob({
        courseId,
        entry: { jobId, type: jobType, startedAt: Date.now(), params: null, total: null },
      }),
    )

  return { activeJob, isBusy, showRun }
}
